import { Injectable } from '@angular/core';
import { RecyclingPoint } from '../../types/recycling-point.interface';
import { getAddressFromEntity } from '../utils/location.utils';

export interface ShareableEntity {
  id: string;
  slug?: string;
  name: string;
  type?: string;          // 'place' | 'container' | 'event' | 'route' etc.
  description?: string;
  address?: any;          // Matches Address type from location.utils
  position?: { lat: number; lng: number };
  materials?: Array<{ label?: string; name?: string }>;
  acceptedMaterials?: string[];
  schedules?: any[];
  isCurrentlyOpen?: boolean;
  contactPhone?: string;
  telefono?: string;
  website?: string;
  sitio_web?: string;
  // Event-specific
  startDate?: string;
  endDate?: string;
  dateRange?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ShareService {

  /**
   * Build the shareable URL for any entity.
   */
  getEntityUrl(entity: ShareableEntity): string {
    const identifier = entity.slug || entity.id;
    return `${window.location.origin}/home/${identifier}`;
  }

  /**
   * Build the address string from an entity using location utils.
   */
  getAddressString(entity: ShareableEntity, options?: { excludeCities?: string[]; fallback?: string }): string {
    return getAddressFromEntity(entity, options);
  }

  /**
   * Generate share text for any entity type.
   */
  generateShareText(entity: ShareableEntity): string {
    if (!entity) return '';

    const icon = this.getEntityIcon(entity.type);
    const url = this.getEntityUrl(entity);
    const address = this.getAddressString(entity);

    let text = `${icon} ${entity.name}\n`;

    if (address) {
      text += `📍 ${address}\n`;
    }

    // Materials
    const materialNames = this.getMaterialNames(entity);
    if (materialNames) {
      text += `🗂️ Materiales: ${materialNames}\n`;
    }

    // Schedule status — only when schedule data exists
    if (entity.schedules && entity.schedules.length > 0) {
      text += entity.isCurrentlyOpen ? '🟢 Abierto ahora\n' : '🔴 Cerrado\n';
    }

    // Event date range
    if (entity.dateRange) {
      text += `🗓️ ${entity.dateRange}\n`;
    }

    // Description (for events/routes)
    if (entity.description && (entity.type === 'event' || entity.type === 'route')) {
      text += `📝 ${entity.description}\n`;
    }

    // Contact
    const phone = entity.contactPhone || entity.telefono;
    if (phone) text += `📞 ${phone}\n`;

    const web = entity.website || entity.sitio_web;
    if (web) text += `🌐 ${web}\n`;

    // text += `\n🔗 ${url}\nCompartido desde Remateriar`;
    text += `\n Compartido desde Remateriar`;
    return text;
  }

  /**
   * Share location (position + name).
   */
  async shareLocation(entity: ShareableEntity): Promise<void> {
    const url = this.getEntityUrl(entity);
    const address = this.getAddressString(entity);
    const locationText = `📍 ${entity.name}\n📍 ${address}`;

    if (navigator.share && entity.position) {
      await navigator.share({
        title: entity.name,
        text: locationText,
        url
      });
    } else {
      await this.copyToClipboard(`${entity.name}\n${address}\n${url}`);
    }
  }

  /**
   * Share full entity info.
   */
  async shareInfo(entity: ShareableEntity): Promise<void> {
    const url = this.getEntityUrl(entity);
    const text = this.generateShareText(entity);

    if (navigator.share) {
      await navigator.share({
        title: entity.name,
        text,
        url
      });
    } else {
      await this.copyToClipboard(text);
    }
  }

  /**
   * Convert a RecyclingPoint to a ShareableEntity.
   */
  fromRecyclingPoint(point: RecyclingPoint): ShareableEntity {
    return {
      id: point.id,
      slug: point.slug,
      name: point.name,
      type: point.type || point.entityType,
      description: point.description,
      address: point.address,
      position: point.position,
      materials: point.materials,
      acceptedMaterials: point.acceptedMaterials,
      schedules: point.schedules,
      isCurrentlyOpen: point.isCurrentlyOpen,
      contactPhone: point.contactPhone,
      telefono: point.telefono,
      website: point.website,
      sitio_web: point.sitio_web,
    };
  }

  // --- Private helpers ---

  private getEntityIcon(type?: string): string {
    switch (type) {
      case 'container':
      case 'street_container':
        return '🗑️';
      case 'event':
        return '📅';
      case 'route':
      case 'mobile_unit':
        return '🚛';
      default:
        return '♻️';
    }
  }

  private getMaterialNames(entity: ShareableEntity): string {
    // Prefer Material objects (with label/name)
    if (entity.materials && entity.materials.length > 0) {
      const names = entity.materials
        .map(m => m.label || m.name)
        .filter(Boolean)
        .join(', ');
      if (names) return names;
    }

    // Fallback to string array
    if (entity.acceptedMaterials && entity.acceptedMaterials.length > 0) {
      return entity.acceptedMaterials.join(', ');
    }

    return '';
  }

  async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }
}
